import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Mail, ArrowLeft, Send, CheckCircle2, AlertCircle, Info, UserX } from 'lucide-react';

function ContactPage() {
  const [subject, setSubject] = useState('suppression');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [description, setDescription] = useState('');
  
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    switch (subject) {
      case 'suppression':
        setDescription(
          "🔒 Suppression de compte RGPD : après validation de votre mot de passe, la suppression est programmée pour 14 jours. Une reconnexion ou le lien reçu par e-mail permet de l’annuler pendant ce délai. Une session d’urgence active doit d’abord être terminée."
        );
        break;
      case 'recuperation':
        setDescription(
          "⚖️ Réquisition judiciaire : Veuillez décrire précisément le contexte de votre demande. Conformément à la loi, la transmission d'historiques ou de données de session est strictement réservée aux officiers de police judiciaire sur présentation d'une réquisition légale."
        );
        break;
      case 'info':
      default:
        setDescription("💬 Demande d'information : Posez-nous votre question ou partagez vos remarques. Notre équipe vous répondra dans les plus brefs délais.");
        break;
    }
  }, [subject]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setFeedback('');

    try {
      const response = await fetch('auth/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ subject, email, message }),
      });

      if (!response.ok) {
        throw new Error('Une erreur réseau est survenue lors de l\'envoi.');
      }

      setStatus('success');
      setFeedback('Votre demande a bien été transmise. Nous vous avons envoyé un accusé de réception par e-mail.');

    } catch (error) {
      console.error('Erreur de soumission:', error);
      setStatus('error');
      setFeedback("Une erreur s'est produite. Si le problème persiste, vous pouvez nous contacter directement à l'adresse support.");
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--mga-navy-deep)', display: 'flex', flexDirection: 'column' }}>
      <Navbar />

      <main style={{ flex: 1, padding: '50px 20px' }}>
        <div style={{ maxWidth: '780px', margin: '0 auto' }}>
          
          {/* Top back button */}
          <div style={{ marginBottom: '25px' }}>
            <Link to="/" className="button-secondary" style={{ display: 'inline-flex', gap: '8px', padding: '10px 18px', fontSize: '0.9rem' }}>
              <ArrowLeft size={16} />
              Retour à l'accueil
            </Link>
          </div>

          {/* Header */}
          <div className="text-center" style={{ marginBottom: '35px' }}>
            <span className="badge-gold">
              <Mail size={16} color="var(--mga-gold)" />
              Support & Droits RGPD
            </span>
            <h1 style={{ fontSize: 'clamp(2rem, 4vw, 2.8rem)', color: 'var(--mga-white)', marginTop: '10px', marginBottom: '10px' }}>
              Contact & Suppression de Compte
            </h1>
            <p style={{ color: 'var(--mga-slate-300)', fontSize: '1.05rem', maxWidth: '600px', margin: '0 auto' }}>
              Formulaire officiel de contact, d'exercice de vos droits RGPD et de demande de suppression définitive de vos données.
            </p>
          </div>

          {/* Main Card Form */}
          <div className="glass-card" style={{
            padding: '40px 35px',
            background: 'rgba(11, 31, 58, 0.45)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
            borderRadius: '24px'
          }}>
            
            {status === 'success' ? (
              <div style={{ textAlign: 'center', padding: '30px 15px' }}>
                <div style={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(16, 185, 129, 0.15)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px auto',
                  color: '#10b981'
                }}>
                  <CheckCircle2 size={40} />
                </div>
                <h2 style={{ color: 'var(--mga-white)', fontSize: '1.8rem', marginBottom: '12px' }}>Demande prise en compte</h2>
                <p style={{ color: 'var(--mga-slate-200)', fontSize: '1.05rem', maxWidth: '500px', margin: '0 auto 30px auto', lineHeight: '1.6' }}>
                  {feedback}
                </p>
                <button 
                  onClick={() => { setStatus('idle'); setEmail(''); setMessage(''); }}
                  className="button-primary"
                >
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                
                {/* Field: Subject */}
                <div>
                  <label htmlFor="subject" style={{ display: 'block', fontWeight: 600, fontSize: '0.95rem', color: 'var(--mga-white)', marginBottom: '8px' }}>
                    Objet de votre demande
                  </label>
                  <select 
                    id="subject" 
                    name="objet" 
                    value={subject} 
                    onChange={(e) => setSubject(e.target.value)} 
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      backgroundColor: 'rgba(5, 11, 20, 0.8)',
                      color: 'var(--mga-white)',
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.98rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="suppression" style={{ backgroundColor: '#050b14', color: '#fff' }}>🗑️ Demande de suppression de compte</option>
                    <option value="recuperation" style={{ backgroundColor: '#050b14', color: '#fff' }}>⚖️ Demande liée à une enquête (récupération)</option>
                    <option value="info" style={{ backgroundColor: '#050b14', color: '#fff' }}>💬 Demande d'informations générales</option>
                  </select>
                </div>

                {/* Description Box */}
                {description && (
                  <div style={{
                    padding: '16px 20px',
                    borderRadius: '14px',
                    backgroundColor: subject === 'suppression' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(242, 184, 7, 0.08)',
                    border: subject === 'suppression' ? '1px solid rgba(239, 68, 68, 0.25)' : '1px solid rgba(242, 184, 7, 0.25)',
                    color: subject === 'suppression' ? '#fca5a5' : 'var(--mga-gold-light)',
                    fontSize: '0.92rem',
                    lineHeight: '1.6',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    {subject === 'suppression' ? <UserX size={20} style={{ flexShrink: 0, marginTop: '2px' }} /> : <Info size={20} style={{ flexShrink: 0, marginTop: '2px' }} />}
                    <div>{description}</div>
                  </div>
                )}

                {/* Field: Email */}
                <div>
                  <label htmlFor="email" style={{ display: 'block', fontWeight: 600, fontSize: '0.95rem', color: 'var(--mga-white)', marginBottom: '8px' }}>
                    Votre adresse e-mail (liée à votre compte)
                  </label>
                  <input 
                    id="email" 
                    type="email" 
                    name="email" 
                    required 
                    placeholder="exemple@email.com"
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)} 
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      backgroundColor: 'rgba(5, 11, 20, 0.8)',
                      color: 'var(--mga-white)',
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.98rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Field: Message */}
                <div>
                  <label htmlFor="message" style={{ display: 'block', fontWeight: 600, fontSize: '0.95rem', color: 'var(--mga-white)', marginBottom: '8px' }}>
                    Détail de votre message
                  </label>
                  <textarea 
                    id="message" 
                    name="message" 
                    rows="6" 
                    required 
                    placeholder="Précisez votre demande ici..."
                    value={message} 
                    onChange={(e) => setMessage(e.target.value)} 
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      backgroundColor: 'rgba(5, 11, 20, 0.8)',
                      color: 'var(--mga-white)',
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.98rem',
                      outline: 'none',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Submit button */}
                <button 
                  type="submit" 
                  className={subject === 'suppression' ? 'button-danger-outline' : 'button-primary'} 
                  disabled={status === 'sending'} 
                  style={{ marginTop: '10px', width: '100%', padding: '14px' }}
                >
                  <Send size={18} />
                  <span>{status === 'sending' ? 'Envoi en cours...' : subject === 'suppression' ? 'Confirmer la demande de suppression' : 'Envoyer le message'}</span>
                </button>
                
                {status === 'error' && (
                  <div style={{
                    padding: '12px 16px',
                    borderRadius: '10px',
                    backgroundColor: 'rgba(239, 68, 68, 0.12)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#f87171',
                    fontSize: '0.9rem',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}>
                    <AlertCircle size={18} />
                    <span>{feedback}</span>
                  </div>
                )}
              </form>
            )}

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default ContactPage;
